import { Data, Modal as ModalModule, UI, Utils } from 'common';
import dompurify from 'dompurify';
import find from 'lodash/find';
import includes from 'lodash/includes';
import isDate from 'lodash/isDate';
import isNaN from 'lodash/isNaN';
import isNull from 'lodash/isNull';
import isNumber from 'lodash/isNumber';
import isString from 'lodash/isString';
import sortBy from 'lodash/sortBy';
import moment from 'moment';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { TimeOptions } from '../shared/TimeOptions';
import HistoryCommunication from '../utils/HistoryCommunication';

const { Carrier } = Data;
const { Modal, ModalButtonActionType } = ModalModule;
const {
	WrappedButton,
	WrappedButtonType,
	WrappedCalendarWithLabel,
	WrappedDropdownWithLabel,
	WrappedInputTextWithLabel,
	WrappedTooltip,
} = UI;
const { Session, withLayoutAndModalContexts, Validation, Util, Analytics } =
	Utils;

// Modal view options
const ViewType = {
	Pickup: 'Pickup',
	PostPickup: 'PostPickup',
};

// Use JavaScript Intl.NumberFormat to format per-print limit value
const usCurrencyFormatter = new Intl.NumberFormat('en-US', {
	style: 'currency',
	currency: 'USD',
});

const ShipFromOptionTemplate = ({ label, tooltip }) => (
	<>
		<div className="ship-from-option">{label}</div>
		<WrappedTooltip
			target="ship-from-option"
			position="bottom"
			tooltip={tooltip}
		/>
	</>
);

class ScheduleCarrierPickupModal extends Component {
	constructor(props) {
		super(props);

		const { data = {} } = props;
		const { prints = [] } = data;
		const { customerDetails = {}, customerId } = Session.getAccountInfo();
		const { email = '', address = {} } = customerDetails;
		const nameParts = isString(address.name) ? address.name.split(' ') : [];
		const firstName = nameParts[0] || '';
		const lastName =
			nameParts.length > 1 ? nameParts[nameParts.length - 1] : '';

		// Use localStorage value to populate default data.
		const localStorageData = localStorage.getItem(
			`schedulePickupDefaultValues_${customerId}`
		);
		const defalutValues = localStorageData ? JSON.parse(localStorageData) : {};

		const carrier = prints[0].printDetails?.carrier;
		const { labelId = '' } = prints[0].printDetails;
		const shipFromOptions = this.buildShipFromOptions(prints);

		/**
		 * "minPickupDate", "maxPickupDate" and "disabledPickupDates" calculation are based on the current logics in the legacy app:
		 * (1) For DHL Express, it will always be the next 6 days regardless of whether prints exist for those days
		 * (2) For UPS, depends on the shipDates in prints. Disable the date if it doesn't exist in prints.
		 */
		const { disabledPickupDates, minPickupDate, maxPickupDate } =
			this.getAvailableShipDates(carrier, prints);

		this.disabledPickupDates = disabledPickupDates;
		this.minPickupDate = minPickupDate;
		this.maxPickupDate = maxPickupDate;

		this.state = {
			// Pickup and Post-pickup fields
			carrier,
			estimatedAmount: 0,
			pickupAddress: shipFromOptions[0]?.value, // use the full address from "tooltip" as a unique key
			pickupDate: minPickupDate,
			shipFromOptions,
			pickupInstructions: '',
			pickupId: '',
			numberOfItemsInPickup: 0,
			labelId,

			// Default values from localStorage
			email: defalutValues.email || email,
			firstName: defalutValues.firstName || firstName,
			lastName: defalutValues.lastName || lastName,
			pickupStartTime: defalutValues.pickupStartTime || 15, // default "3:00 PM"
			pickupEndTime: defalutValues.pickupEndTime || 19, // default "7:00 PM"
			phoneNumber: defalutValues.phoneNumber || address.phone,

			// Required fields for validation
			validations: {
				// 0 = untouched, -1 = failed, 1 = valid
				pickupDate: 0,
				pickupEndTime: 0,
				pickupInstructions: 0,
				email: 0,
				phoneNumber: 0,
			},
			viewType: ViewType.Pickup,
			isSchedulePickupClicked: false,
		};
	}

	componentDidMount() {
		// TODO: make a call to CheckCarrierPickupAvailability SWSIM API to update "estimatedAmount".
	}

	/**
	 * Build "Where" dropdown options (format : <ratingFullName> - <ratingCity>, <ratingState>)
	 * @returns {array}
	 */
	buildShipFromOptions = prints =>
		prints.reduce((_result, print) => {
			const { printDetails = {} } = print;
			const { fromAddress = {} } = printDetails;
			const {
				addressLine1,
				companyName,
				city,
				name,
				stateProvince,
				postalCode,
			} = fromAddress;
			const tooltip = [
				'<p>',
				name,
				companyName,
				`${addressLine1},`,
				`${city}, ${stateProvince} ${postalCode}`,
				'</p>',
			]
				.filter(str => isString(str) && str !== '')
				.join('<br>');

			if (!find(_result, { tooltip })) {
				_result.push({
					label: `${name} - ${city}, ${stateProvince}`,
					value: tooltip, // use tooltip as the unique key
					tooltip,
					fromAddress,
				});
			}
			return _result;
		}, []);

	/**
	 * Build the payload for /pickups endpoint
	 * @returns {object}
	 */
	buildSchedulePickupPayload = () => {
		const {
			pickupAddress,
			shipFromOptions,
			pickupDate,
			pickupStartTime,
			pickupEndTime,
			pickupInstructions,
			carrier,
			email,
			labelId,
		} = this.state;

		const selectedPickupAddress =
			shipFromOptions.find(option => option.value === pickupAddress)
				?.fromAddress || {};

		const startTime = moment(pickupDate)
			.hour(pickupStartTime)
			.utc()
			.format('YYYY-MM-DDTHH:mm:ss');
		const endTime = moment(pickupDate)
			.hour(pickupEndTime)
			.utc()
			.format('YYYY-MM-DDTHH:mm:ss');

		return {
			pickup_address: {
				company_name: selectedPickupAddress.companyName,
				name: selectedPickupAddress.name,
				address_line1: selectedPickupAddress.addressLine1,
				city: selectedPickupAddress.city,
				state_province: selectedPickupAddress.stateProvince,
				postal_code: selectedPickupAddress.postalCode,
				country_code: selectedPickupAddress.countryCode,
				phone: selectedPickupAddress.phone,
				email: isNull(selectedPickupAddress.email)
					? email
					: selectedPickupAddress.email,
			},
			carrier,
			label_ids: [labelId],
			pickup_window: {
				start_at: startTime,
				end_at: endTime,
			},
			pickup_instructions: pickupInstructions,
		};
	};

	/**
	 * Calculate the disabled pickup dates from prints
	 * @param {array} prints array of prints sorted by "shipDate"
	 * @returns {array} array of dates that are not available for pickup.
	 */
	calculateDisabledPickupDates = prints => {
		// all the available pickup dates in "YYYY-MM-DD" string format
		const sortedPickupDates = prints.map(({ printDetails = {} }) => {
			const { shipDate } = printDetails;

			return isDate(shipDate) ? moment(shipDate).format('YYYY-MM-DD') : '';
		});

		/**
		 * Calculate the dates that are not available for pickup.
		 *
		 * Start with the earliest pickup date and add one day at a time.
		 * If the new date does not exist in the available pickup dates("sortedPickupDates"),
		 * then it will be added to the "disabledPickupDates"
		 */
		const nextPickupDate = moment(sortedPickupDates[0]);
		const maxPickupDate = moment(
			sortedPickupDates[sortedPickupDates.length - 1]
		);
		const disabledPickupDates = [];

		// End if "nextPickupDate" is later than "maxPickupDate"
		while (nextPickupDate.isBefore(maxPickupDate)) {
			// Add one day to the next date.
			nextPickupDate.add(1, 'days');

			// Add to disabled dates if it is not in one of the pickup dates.
			if (
				sortedPickupDates.indexOf(nextPickupDate.format('YYYY-MM-DD')) === -1
			) {
				disabledPickupDates.push(nextPickupDate.toDate());
			}
		}

		return disabledPickupDates;
	};

	/**
	 * Get disabled pickup dates, min pickup date and max pickup date based on carrier
	 * @param {Carrier} carrier
	 * @param {array} prints
	 * @returns {object}
	 * - disabledPickupDates {array} dates that will be disabled from selecting
	 * - minPickupDate {date}
	 * - maxPickupDate {date}
	 */
	getAvailableShipDates = (carrier, prints = []) => {
		let disabledPickupDates = [];
		let minPickupDate = null;
		let maxPickupDate = null;

		// Sort the dates in ascending order
		const sortedPrints = sortBy(prints, ({ printDetails = {} }) =>
			printDetails.shipDate?.getTime()
		);

		if (carrier === Carrier.DHLExpress) {
			// ship dates for DHL Express will always be the next 6 days regardless of whether dates exist in the prints
			minPickupDate = moment().add(1, 'days').toDate();
			maxPickupDate = moment().add(6, 'days').toDate();
		} else if (carrier === Carrier.UPS) {
			// ship dates available for selecting depends on whether dates exist in the prints
			disabledPickupDates = this.calculateDisabledPickupDates(sortedPrints);
			minPickupDate = sortedPrints[0]?.printDetails?.shipDate;
			maxPickupDate =
				sortedPrints[sortedPrints.length - 1]?.printDetails?.shipDate;
		}

		return {
			disabledPickupDates,
			minPickupDate,
			maxPickupDate,
		};
	};

	/**
	 * Validate the required fields in the form
	 * @returns {boolean} false if any of the required field is -1
	 */
	validateForm = () => {
		const {
			pickupDate,
			pickupStartTime,
			pickupEndTime,
			pickupInstructions,
			email,
			phoneNumber,
		} = this.state;

		const validations = {
			pickupDate: pickupDate ? 1 : -1,
			pickupEndTime: pickupEndTime - pickupStartTime > 1 ? 1 : -1,
			pickupInstructions: pickupInstructions.length > 0 ? 1 : -1,
			email: Validation.isValidEmail(email) ? 1 : -1,
			phoneNumber: phoneNumber.length > 0 ? 1 : -1,
		};

		this.setState({
			validations,
		});

		return includes(validations, -1) === false;
	};

	/**
	 * Validate the required fields
	 * @param {object} event object
	 */
	onBlur = ({ target }) => {
		const { name, value } = target;
		const { validations } = this.state;
		const newValidations = { ...validations };

		if (name === 'email') {
			newValidations.email = Validation.isValidEmail(value) ? 1 : -1;
		} else {
			newValidations[name] = value.length > 0 ? 1 : -1;
		}

		this.setState({
			validations: newValidations,
		});
	};

	/**
	 * Validate "pickupStartTime" and "pickupEndTime" fields
	 */
	onBlurPickupTime = () => {
		this.setState(({ validations, pickupStartTime, pickupEndTime }) => ({
			validations: {
				...validations,
				pickupEndTime: pickupEndTime - pickupStartTime > 1 ? 1 : -1,
			},
		}));
	};

	/**
	 * Change the value of the fields
	 * @param {object} event object
	 */
	onChange = ({ target }) => {
		const { name, value } = target;

		this.setState({
			[name]: value,
		});
	};

	onClickCancel = () => {
		const { modalId, modalContext } = this.props;

		modalContext.closeModal(modalId);
	};

	/**
	 * onClick handler for "Schedule Pickup" button in Pickup modal view
	 */
	onClickSchedulePickupButton = async () => {
		const { modalContext } = this.props;
		const { showError } = modalContext;
		const {
			email,
			firstName,
			lastName,
			phoneNumber,
			pickupEndTime,
			pickupStartTime,
		} = this.state;
		const isValid = this.validateForm();
		const { customerId } = Session.getAccountInfo();

		if (isValid) {
			// Store the following information in localStorage
			localStorage.setItem(
				`schedulePickupDefaultValues_${customerId}`,
				JSON.stringify({
					email,
					firstName,
					lastName,
					phoneNumber,
					pickupEndTime,
					pickupStartTime,
				})
			);

			this.setState({
				isSchedulePickupClicked: true,
			});

			const payload = this.buildSchedulePickupPayload();
			const response = await HistoryCommunication.schedulePickup(payload);

			if (!HistoryCommunication.isSuccessful(response)) {
				this.setState({
					isSchedulePickupClicked: false,
				});

				const error = {
					errorCode: response?.error?.errorCode,
					errorMessage: isString(response?.error?.errorMessage)
						? response.error.errorMessage
						: response.ErrorDescription,
				};

				Analytics.trackEvent(
					'carrierPickup',
					{
						carrier: payload.carrier,
						success: false,
						error: response,
					},
					Analytics.agent.segment
				);

				return showError({
					data: {
						// display the error code
						code:
							isNumber(error.errorCode) && !isNaN(error.errorCode)
								? error.errorCode
								: null,
						title: !isNumber(error.errorCode) ? 'Error' : null,
						description: `<p>${error.errorMessage}</p>`,
						primaryButtonText: 'OK',
						handleType: 'custom',
					},
				});
			}

			const data = Util.toCamelCase(response.data);

			Analytics.trackEvent(
				'carrierPickup',
				{
					carrier: payload.carrier,
					success: true,
				},
				Analytics.agent.segment
			);

			this.setState({
				viewType: ViewType.PostPickup,
				pickupId: data.pickupId,
				numberOfItemsInPickup: data.numberOfItemsInPickup,
			});
		}
	};

	onSelectDate = ({ value }) => {
		this.setState(({ validations }) => ({
			validations: {
				...validations,
				pickupDate: value ? 1 : -1,
			},
		}));
	};

	/**
	 * Render both UPS and DHL Express's Pickup Information.
	 * @returns {ReactElement}
	 */
	renderPickupInfo = () => {
		const {
			layoutContext: { isMobile },
		} = this.props;
		const {
			pickupAddress,
			pickupDate,
			pickupStartTime,
			pickupEndTime,
			pickupInstructions,
			shipFromOptions,
			validations,
		} = this.state;
		const pickupStartOptions = TimeOptions.slice(0, TimeOptions.length - 1);
		const pickupEndOptions = TimeOptions.slice(pickupStartTime + 1);

		return (
			<div className="p-grid p-nogutter">
				<p className="p-col-12">
					<b>Pickup Information</b>
				</p>
				<hr className="p-col-12" />
				<WrappedCalendarWithLabel
					className="p-col-6 p-md-5"
					isInputInvalid={validations.pickupDate === -1}
					errorMessage="Pickup Date is required."
					label="When:"
					showErrorBelow={isMobile}
					uiElementConfig={{
						value: pickupDate,
						dataqa: 'pickupDate',
						name: 'pickupDate',
						selectOtherMonths: true,
						minDate: this.minPickupDate,
						maxDate: this.maxPickupDate,
						disabledDates: this.disabledPickupDates,
						onBlur: this.onBlur,
						onSelect: this.onSelectDate,
						onChange: this.onChange,
					}}
				/>
				<WrappedDropdownWithLabel
					className="p-col-2 pickup-time-field"
					uiElementConfig={{
						value: pickupStartTime,
						options: pickupStartOptions,
						dataqa: 'pickup-start-time',
						name: 'pickupStartTime',
						onBlur: this.onBlurPickupTime,
						onChange: this.onChange,
					}}
				/>
				<WrappedDropdownWithLabel
					className="p-col-4 pickup-time-field"
					isInputInvalid={validations.pickupEndTime === -1}
					errorMessage="Pickup timeframe must be at least two hours long"
					label="to"
					uiElementConfig={{
						value: pickupEndTime,
						options: pickupEndOptions,
						dataqa: 'pickup-end-time',
						name: 'pickupEndTime',
						onBlur: this.onBlurPickupTime,
						onChange: this.onChange,
					}}
				/>
				<div className="p-col-12 p-md-8">
					<WrappedDropdownWithLabel
						className=""
						label="Where:"
						showErrorBelow={isMobile}
						uiElementConfig={{
							value: pickupAddress,
							options: shipFromOptions,
							dataqa: 'pickup-address',
							name: 'pickupAddress',
							itemTemplate: ShipFromOptionTemplate,
							onChange: this.onChange,
						}}
					/>
					<WrappedInputTextWithLabel
						className="p-col-12"
						errorMessage="Pickup Instructions are required."
						isInputInvalid={validations.pickupInstructions === -1}
						label="Pickup Instructions:"
						showErrorBelow={isMobile}
						uiElementConfig={{
							value: pickupInstructions,
							dataqa: 'pickupInstructions',
							name: 'pickupInstructions',
							onChange: this.onChange,
							onBlur: this.onBlur,
						}}
					/>
				</div>
				<div className="p-col-12 p-md-4">
					<div
						className="address-field"
						// eslint-disable-next-line
						dangerouslySetInnerHTML={{
							__html: isString(pickupAddress)
								? dompurify.sanitize(pickupAddress)
								: '',
						}}
					/>
				</div>
			</div>
		);
	};

	/**
	 * Render UPS Pickup View - Contact Information and footer view.
	 * @returns {ReactElement}
	 */
	renderUpsPickupView = () => {
		const {
			layoutContext: { isMobile },
			data = {},
		} = this.props;
		const {
			email,
			estimatedAmount,
			phoneNumber,
			validations,
			isSchedulePickupClicked,
		} = this.state;
		const { prints } = data;

		return (
			<>
				{this.renderPickupInfo()}
				<div className="p-grid p-nogutter">
					<p className="p-col-12">
						<b>Contact Information</b>
					</p>
					<hr className="p-col-12" />
					<div className="p-col-12">
						<div className="p-grid p-dir-col p-nogutter">
							<WrappedInputTextWithLabel
								className="p-col-12 p-md-6"
								isInputInvalid={validations.phoneNumber === -1}
								errorMessage="Phone number is required."
								label="Telephone:"
								showErrorBelow={isMobile}
								uiElementConfig={{
									value: Util.formatPhoneNumber(phoneNumber),
									dataqa: 'phone',
									name: 'phoneNumber',
									maxLength: 23,
									required: true,
									keyfilter: 'pint',
									onBlur: this.onBlur,
									onChange: this.onChange,
								}}
							/>
							<WrappedInputTextWithLabel
								className="p-col-12 p-md-6"
								isInputInvalid={validations.email === -1}
								errorMessage={
									Validation.isValidEmail(email) === false
										? 'Invalid Email address.'
										: 'Email is required.'
								}
								label="Email:"
								showErrorBelow={isMobile}
								uiElementConfig={{
									value: email,
									dataqa: 'customer-email',
									name: 'email',
									onBlur: this.onBlur,
									onChange: this.onChange,
								}}
							/>
						</div>
					</div>
				</div>
				<div className="p-grid p-nogutter">
					<div className="p-col-12 pickup-cost">
						<span>Eligible Packages: {prints.length}</span>
					</div>
					<div className="footer">
						<WrappedButton
							buttonType={WrappedButtonType.Primary}
							onClick={this.onClickSchedulePickupButton}
							label="Schedule Pickup"
							disabled={isSchedulePickupClicked}
							dataqa="schedule-carrier-pickup-schedule"
						/>
						<WrappedButton
							buttonType={WrappedButtonType.Secondary}
							label="Cancel"
							onClick={this.onClickCancel}
							dataqa="schedule-carrier-pickup-cancel"
						/>
					</div>
				</div>
			</>
		);
	};

	/**
	 * Render UPS - 'PostPickup' view.
	 * @returns {ReactElement}
	 */
	renderUpsPostPickupView = () => {
		const {
			pickupAddress,
			pickupDate,
			pickupStartTime,
			pickupEndTime,
			pickupId,
			numberOfItemsInPickup,
			pickupInstructions,
		} = this.state;

		return (
			<div className="p-grid">
				<p className="p-col-12">
					Your UPS Pickup has been scheduled. Here are your pickup details:
				</p>
				<p className="p-col-4 post-pickup-field-name">Pickup Date/Time:</p>
				<p className="p-col-8">
					{`${moment(pickupDate).format('dddd, MM/DD/YYYY')} ${
						TimeOptions[pickupStartTime].label
					} - ${TimeOptions[pickupEndTime].label}`}
				</p>
				<p className="p-col-4 post-pickup-field-name">Pickup Instructions:</p>
				<p className="p-col-8">{pickupInstructions}</p>
				<p className="p-col-4 post-pickup-field-name">Number of Packages:</p>
				<p className="p-col-8">{numberOfItemsInPickup}</p>
				<p className="p-col-4 post-pickup-field-name">Confirmation Number:</p>
				<p className="p-col-8">{pickupId}</p>
				<p className="p-col-4 post-pickup-field-name">Pickup Address:</p>
				<div className="p-col-8">
					<div
						// eslint-disable-next-line
						dangerouslySetInnerHTML={{
							__html: isString(pickupAddress)
								? dompurify.sanitize(pickupAddress)
								: '',
						}}
					/>
				</div>
			</div>
		);
	};

	/**
	 * Render DHL Express Pickup View - Contact Information and footer view.
	 * @returns {ReactElement}
	 */
	renderDhlPickupView = () => {
		const {
			layoutContext: { isMobile },
		} = this.props;
		const { estimatedAmount, phoneNumber, validations } = this.state;

		return (
			<>
				{this.renderPickupInfo()}
				<div className="p-grid p-nogutter">
					<p className="p-col-12 section-header">
						<b>Contact Information</b>
					</p>
					<hr className="p-col-12" />
					<div className="p-col-12">
						<div className="p-grid p-nogutter">
							<WrappedInputTextWithLabel
								className="p-col-12 p-md-6"
								isInputInvalid={validations.phoneNumber === -1}
								errorMessage="Phone number is required."
								label="Telephone:"
								showErrorBelow={isMobile}
								uiElementConfig={{
									value: Util.formatPhoneNumber(phoneNumber),
									dataqa: 'phone',
									name: 'phoneNumber',
									maxLength: 23,
									required: true,
									keyfilter: 'pint',
									onBlur: this.onBlur,
									onChange: this.onChange,
								}}
							/>
						</div>
					</div>
				</div>
				<div className="p-grid p-nogutter">
					<div className="p-col-12 pickup-cost">
						<span>
							Pickup Cost:{' '}
							{estimatedAmount === 0
								? 'Free'
								: usCurrencyFormatter.format(estimatedAmount)}
						</span>
					</div>
				</div>
			</>
		);
	};

	/**
	 * Render DHL Express 'PostPickup' view.
	 * @returns {ReactElement}
	 */
	renderDhlPostPickupView = () => {
		const {
			pickupAddress,
			pickupDate,
			pickupStartTime,
			pickupEndTime,
			pickupId,
		} = this.state;

		return (
			<div className="p-grid">
				<p className="p-col-12">
					Your DHL Express pickup has been scheduled. Here are your pickup
					details:
				</p>
				<p className="p-col-4 post-pickup-field-name">Pickup Date/Time:</p>
				<p className="p-col-8">
					{`${moment(pickupDate).format('dddd, MM/DD/YYYY')} ${
						TimeOptions[pickupStartTime].label
					} - ${TimeOptions[pickupEndTime].label}`}
				</p>
				<p className="p-col-4 post-pickup-field-name">Confirmation Number:</p>
				<p className="p-col-8">{pickupId}</p>
				<p className="p-col-4 post-pickup-field-name">Pickup Address:</p>
				<div className="p-col-8">
					<div
						// eslint-disable-next-line
						dangerouslySetInnerHTML={{
							__html: isString(pickupAddress)
								? dompurify.sanitize(pickupAddress)
								: '',
						}}
					/>
				</div>
			</div>
		);
	};

	renderCarrierPickupView = () => {
		const { carrier, viewType } = this.state;
		const isUps = carrier === Carrier.UPS;

		if (viewType === ViewType.Pickup) {
			return isUps ? this.renderUpsPickupView() : this.renderDhlPickupView();
		} else if (viewType === ViewType.PostPickup) {
			return isUps
				? this.renderUpsPostPickupView()
				: this.renderDhlPostPickupView();
		}
	};

	render() {
		const { modalId, data = {} } = this.props;
		const { carrier, viewType } = this.state;
		const { onClickManagePickupsModalButton } = data;

		const primaryButton =
			viewType === ViewType.Pickup
				? {
						type: ModalButtonActionType.Primary,
						text: 'Schedule Pickup',
						onClick: this.onClickSchedulePickupButton,
					}
				: {
						type: ModalButtonActionType.Ok,
						text: 'Done',
					};

		const secondaryButton =
			viewType === ViewType.Pickup
				? {
						type: ModalButtonActionType.Ok,
						text: 'Cancel',
					}
				: {
						type: ModalButtonActionType.Ok,
						text: 'Manage Pickups',
						onClick: onClickManagePickupsModalButton,
					};

		// Custom footer for UPS Pickup view
		const hideFooter = carrier === Carrier.UPS && viewType === ViewType.Pickup;

		return (
			<Modal
				modalId={modalId}
				className="history-pickup schedule-carrier-pickup-modal"
				header={`Schedule ${
					carrier === Carrier.UPS ? 'UPS' : 'DHL Express'
				} Pickup - Details`}
				primaryButton={primaryButton}
				secondaryButton={secondaryButton}
				hideFooter={hideFooter}
			>
				{this.renderCarrierPickupView()}
			</Modal>
		);
	}
}

ScheduleCarrierPickupModal.propTypes = {
	data: PropTypes.shape({
		// All eligible packages
		prints: PropTypes.array,

		onClickManagePickupsModalButton: PropTypes.func,
	}),
};

export default withLayoutAndModalContexts(ScheduleCarrierPickupModal);
